import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'
import { hasPermission } from '@/constants/permissions'
import { getUser } from '@/utils/auth'
import type { UserRole } from '@/types'

type GuardRule = {
  pathPrefix: string
  permission: string
  label: string
}

const RULES: GuardRule[] = [
  { pathPrefix: ROUTES.SHIPMENTS, permission: 'shipments:view', label: 'Shipments' },
  { pathPrefix: ROUTES.CLEARING, permission: 'clearing:view', label: 'Clearing' },
  { pathPrefix: ROUTES.INVENTORY, permission: 'inventory:view', label: 'Storage & Inventory' },
  { pathPrefix: ROUTES.LOAD_OUT, permission: 'load_out:view', label: 'Stock Dispatch' },
  { pathPrefix: '/finance', permission: 'payments:view', label: 'Finance' },
  { pathPrefix: ROUTES.CONTRACTS, permission: 'contracts:view', label: 'Contract File' },
  { pathPrefix: ROUTES.REPORTS, permission: 'reports:view', label: 'Reports' },
  { pathPrefix: ROUTES.SETTINGS_USER_MANAGEMENT, permission: 'users:view', label: 'User Management' },
]

const normalizePath = (path: string) => path.split('?')[0]

function getRequiredRule(pathname: string): GuardRule | null {
  const clean = normalizePath(pathname)
  return RULES.find((rule) => clean === rule.pathPrefix || clean.startsWith(`${rule.pathPrefix}/`)) || null
}

export default function RoleAccessGuard({ children }: { children: ReactNode }) {
  const location = useLocation()
  const user = getUser()
  const requiredRule = getRequiredRule(location.pathname)

  if (!requiredRule || !user?.role) {
    return <>{children}</>
  }

  const canView = hasPermission(user.role as UserRole, requiredRule.permission)
  if (canView) {
    return <>{children}</>
  }

  return (
    <div className="min-h-[calc(100vh-180px)] flex items-center justify-center px-4">
      <div
        className="w-full max-w-xl rounded-xl border border-gray-200 bg-white p-6 sm:p-8 text-center"
        style={{ fontFamily: 'Lexend Deca' }}
      >
        <h2 className="text-xl font-semibold" style={{ color: '#212126' }}>
          Access Restricted
        </h2>
        <p className="mt-3 text-sm" style={{ color: '#69697A', lineHeight: '22px' }}>
          Your role does not have access to <strong>{requiredRule.label}</strong>.
          If this seems incorrect, contact an administrator.
        </p>
        <div className="mt-6">
          <Link
            to={ROUTES.DASHBOARD}
            className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#212126' }}
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}

